import { useState } from 'react'
import {
  App, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space,
  Switch, Tag, Typography,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { shutdownPlansApi } from '@/features/maintenance/api'
import { machinesApi } from '@/features/masters/machines/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { ShutdownPlan } from '@/shared/types/maintenance'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import AuditTrailDrawer from './components/AuditTrailDrawer'

const { Text } = Typography

/**
 * Spec §12 — Shutdown Plan.
 * Manager creates the plan; Production Sup + Plant Head approve. Status
 * automatically transitions to APPROVED when both gates pass.
 */
export default function ShutdownPlanPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canCreate = usePermission('maintenance.create')
  const canApprove = usePermission('maintenance.update')

  const [createOpen, setCreateOpen] = useState(false)
  const [approving, setApproving] = useState<{ plan: ShutdownPlan; role: 'production' | 'plant_head' } | null>(null)
  const [auditEntityId, setAuditEntityId] = useState<string | null>(null)

  const { data: plans, isLoading } = useQuery({
    queryKey: ['shutdown-plans', plantId],
    enabled: !!plantId,
    queryFn: () => shutdownPlansApi.list({ plant_id: plantId, limit: 200 }),
  })

  const { data: machinesData } = useQuery({
    queryKey: ['machines-for-maint', plantId],
    enabled: !!plantId,
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 500 }),
  })
  const machines = machinesData?.items ?? []

  const [createForm] = Form.useForm()
  const createMutation = useMutation({
    mutationFn: (vals: {
      machine_ids: string[]
      planned_range: [dayjs.Dayjs, dayjs.Dayjs]
      required_manpower?: number
      safety_permit_required?: boolean
      remarks?: string
    }) => shutdownPlansApi.create({
      plant_id: plantId,
      machine_ids: vals.machine_ids,
      planned_start: vals.planned_range[0].toISOString(),
      planned_end:   vals.planned_range[1].toISOString(),
      required_manpower: vals.required_manpower,
      safety_permit_required: vals.safety_permit_required,
      remarks: vals.remarks,
      job_list_json: [],
      required_spares_json: [],
    }),
    onSuccess: (p) => {
      message.success(`${p.shutdown_plan_no} created — pending approvals`)
      qc.invalidateQueries({ queryKey: ['shutdown-plans'] })
      setCreateOpen(false)
    },
    onError: () => message.error('Create failed'),
  })

  const [approveForm] = Form.useForm()
  const approveMutation = useMutation({
    mutationFn: (vals: { approved: boolean; remarks?: string }) =>
      shutdownPlansApi.approve(approving!.plan.id, {
        role: approving!.role,
        approved: vals.approved,
        remarks: vals.remarks,
      }),
    onSuccess: () => {
      message.success('Decision recorded')
      qc.invalidateQueries({ queryKey: ['shutdown-plans'] })
      setApproving(null)
    },
  })

  const columns = [
    { title: 'Plan No', dataIndex: 'shutdown_plan_no', key: 'no', width: 130 },
    {
      title: 'Planned Start', dataIndex: 'planned_start', key: 'ps', width: 140,
      render: (v: string) => dayjs(v).format('DD MMM HH:mm'),
    },
    {
      title: 'Planned End', dataIndex: 'planned_end', key: 'pe', width: 140,
      render: (v: string) => dayjs(v).format('DD MMM HH:mm'),
    },
    {
      title: 'Machines', dataIndex: 'machine_ids', key: 'mids', width: 100,
      render: (v: string[]) => v.length,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'st', width: 110,
      render: (v: string) => <MaintenanceStatusBadge status={v} />,
    },
    {
      title: 'Production Approval', dataIndex: 'production_approval_status', key: 'pa', width: 160,
      render: (v: string) => <Tag color={v === 'APPROVED' ? 'green' : v === 'REJECTED' ? 'red' : 'default'}>{v}</Tag>,
    },
    {
      title: 'Plant Head Approval', dataIndex: 'plant_head_approval_status', key: 'pha', width: 160,
      render: (v: string) => <Tag color={v === 'APPROVED' ? 'green' : v === 'REJECTED' ? 'red' : 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 240,
      render: (_: unknown, r: ShutdownPlan) => (
        <Space size={4}>
          {canApprove && r.production_approval_status === 'PENDING' && (
            <Button size="small" type="link" onClick={() => {
              approveForm.resetFields()
              approveForm.setFieldsValue({ approved: true })
              setApproving({ plan: r, role: 'production' })
            }}>Production review</Button>
          )}
          {canApprove && r.plant_head_approval_status === 'PENDING' && (
            <Button size="small" type="link" onClick={() => {
              approveForm.resetFields()
              approveForm.setFieldsValue({ approved: true })
              setApproving({ plan: r, role: 'plant_head' })
            }}>Plant head review</Button>
          )}
          <Button size="small" type="link" onClick={() => setAuditEntityId(r.id)}>Audit</Button>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Shutdown Plans"
        subtitle="Plan and seek dual approval (Production + Plant Head) for shutdown maintenance windows."
        extra={canCreate && (
          <Button type="primary" onClick={() => {
            createForm.resetFields()
            createForm.setFieldsValue({ planned_range: [dayjs(), dayjs().add(1, 'day')] })
            setCreateOpen(true)
          }}>New plan</Button>
        )}
      />

      <DataTable rowKey="id" columns={columns} dataSource={plans} loading={isLoading} />

      <Modal
        title="New shutdown plan"
        open={createOpen}
        onOk={() => createForm.validateFields().then((v) => createMutation.mutate(v))}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={createMutation.isPending}
        width={640}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item name="machine_ids" label="Machines covered" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              showSearch
              options={machines.map((m) => ({ label: `${m.machine_id_str} — ${m.name}`, value: m.id }))}
              filterOption={(i, o) => (o?.label as string).toLowerCase().includes(i.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="planned_range" label="Planned window" rules={[{ required: true }]}>
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="required_manpower" label="Required manpower">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="safety_permit_required" label="Safety permit required" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Plan moves to APPROVED automatically when Production AND Plant Head approve.
          </Text>
        </Form>
      </Modal>

      <Modal
        title={approving ? `${approving.role === 'production' ? 'Production' : 'Plant Head'} review — ${approving.plan.shutdown_plan_no}` : ''}
        open={!!approving}
        onOk={() => approveForm.validateFields().then((v) => approveMutation.mutate(v))}
        onCancel={() => setApproving(null)}
        confirmLoading={approveMutation.isPending}
      >
        <Form form={approveForm} layout="vertical">
          <Form.Item name="approved" label="Decision" valuePropName="checked">
            <Switch checkedChildren="Approve" unCheckedChildren="Reject" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <AuditTrailDrawer
        entityId={auditEntityId}
        title="Shutdown Plan Audit"
        open={auditEntityId !== null}
        onClose={() => setAuditEntityId(null)}
      />
    </div>
  )
}
